import { useTheme } from 'styled-components';
import * as S from './styles';
import { unthemed } from 'tokens';

export enum ButtonState {
	REGULAR, // regular state, clicking performs an action
	DISABLED, // not clickable.
}

export enum ButtonType {
	PRIMARY,
	SECONDARY,
	NOTICEABLE,
}

export enum ButtonSize {
	MEDIUM = 32,
}

interface ButtonProps {
	// user-facing label of the button
	label?: string;

	// if not provided, defaults to ButtonState.REGULAR
	state?: ButtonState;

	// if not provided, defaults to ButtonType.PRIMARY
	type?: ButtonType;

	// if not provided, defaults to ButtonSize.MEDIUM
	size?: ButtonSize;

	// optional width in px
	width?: number;

	// this will be called when the button is clicked, if the state is not disabled
	onSelected?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

	children?: React.ReactNode;
}

const Button = ({
	label,
	state = ButtonState.REGULAR,
	type,
	size = ButtonSize.MEDIUM,
	width,
	onSelected,
	onClick,
	children,
}: ButtonProps) => {
	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onButtonPress = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (state !== ButtonState.DISABLED) {
			onSelected?.(e);
			onClick?.(e);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const theme = useTheme();

	// set defaults to match PRIMARY / REGULAR config
	let backgroundColor = theme.gray['1000'];
	let activeBackgroundColor = theme.gray['400'];
	let hoverBackgroundColor = theme.gray['700'];
	let borderColor = 'transparent';
	let labelColor = theme.gray['0'];

	switch (type) {
		case ButtonType.PRIMARY:
			switch (state) {
				case ButtonState.REGULAR:
					// defaults suffice
					break;
				case ButtonState.DISABLED:
					backgroundColor = theme.gray['100'];
					activeBackgroundColor = theme.gray['100'];
					hoverBackgroundColor = theme.gray['100'];
					borderColor = theme.gray['400'];
					labelColor = theme.gray['400'];
					break;
			}
			break;
		case ButtonType.SECONDARY:
			switch (state) {
				case ButtonState.REGULAR:
					backgroundColor = theme.gray['0'];
					activeBackgroundColor = theme.gray['100'];
					hoverBackgroundColor = theme.gray['200'];
					borderColor = theme.gray['200'];
					labelColor = theme.gray['1000'];
					break;
				case ButtonState.DISABLED:
					backgroundColor = theme.gray['0'];
					activeBackgroundColor = theme.gray['0'];
					hoverBackgroundColor = theme.gray['0'];
					borderColor = theme.gray['200'];
					labelColor = theme.gray['400'];
					break;
			}
			break;
		case ButtonType.NOTICEABLE:
			backgroundColor = unthemed.blue['500'];
			// yes this is very unorthodox!
			// the blue tokens are not very good, so i'm hardcoding some values here
			// since this is unthemed, hardcoding should be OK
			// don't wanna take the time right now to clean up our blue colors - ss
			activeBackgroundColor = unthemed.blue['700'];
			hoverBackgroundColor = unthemed.blue['600'];
			borderColor = 'transparent';
			labelColor = unthemed.gray['0'];
			break;
	}

	return (
		<S.Base
			backgroundColor={backgroundColor}
			activeBackgroundColor={activeBackgroundColor}
			hoverBackgroundColor={hoverBackgroundColor}
			borderColor={borderColor}
			labelColor={labelColor}
			width={width ? width + 'px' : '60px'}
			height={size}
			fontSize={12}
			state={state}
			onClick={onButtonPress}
		>
			{label || children}
		</S.Base>
	);
};

export default Button;
